// ======================================================================
// FILE: src/modules/wms/checklist/pages/ChecklistFormPage.js
// MODULE: Work Management System (WMS)
// FEATURE: Checklist Management
//
// PURPOSE:
//   Create / Edit Checklist
//   Manage Checklist Sections
//   Single Master Screen
//
// ROUTES:
//   /wms/checklists/create
//   /wms/checklists/:id/edit
//
// DEPENDENCIES:
//   wmsChecklistService
//   ChecklistSectionsTab
//
// ARCHITECTURE:
//   ✓ TaskFlow ERP Standard
//   ✓ AdminLTE
//   ✓ Multi Tenant
//   ✓ Multi Location
//   ✓ Audit Enabled
// ======================================================================

import React, {
  useEffect,
  useState,
} from "react";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import wmsChecklistService
from "../../services/wmsChecklistService";

import ChecklistSectionsTab
from "../components/ChecklistSectionsTab";

export default function ChecklistFormPage() {

  const navigate = useNavigate();

  const { id } = useParams();

  const isEdit = !!id;

  const [saving, setSaving] =
    useState(false);

  const [loading, setLoading] =
    useState(false);

  const [form, setForm] =
    useState({

      checklist_code: "",
      checklist_name: "",

      applicable_work_item_type:
        "TASK",

      category: "",

      //department_id: "",

      pass_percentage: "",

      is_scoring_enabled: false,

      approval_required: false,

      is_global_checklist: false,

      description: "",

      remarks: "",
    });

  // ==========================================================
  // LOAD
  // ==========================================================

  useEffect(() => {

    if (!isEdit) {
      return;
    }

    loadChecklist();

  }, [id]);

  async function loadChecklist() {

    try {

      setLoading(true);

      const data =
        await wmsChecklistService
          .getChecklist(id);

      setForm({

        checklist_code:
          data.checklist_code || "",

        checklist_name:
          data.checklist_name || "",

        applicable_work_item_type:
          data.applicable_work_item_type
          || "TASK",

        category:
          data.category || "",

        //department_id:
        //  data.department_id || "",

        pass_percentage:
          data.pass_percentage || "",

        is_scoring_enabled:
          data.is_scoring_enabled
          || false,

        approval_required:
          data.approval_required
          || false,

        is_global_checklist:
          data.is_global_checklist
          || false,

        description:
          data.description || "",

        remarks:
          data.remarks || "",
      });

    } catch (err) {

      alert(
        err?.response?.data?.message
        || "Failed to load checklist"
      );

    } finally {

      setLoading(false);
    }
  }

  // ==========================================================
  // CHANGE
  // ==========================================================

  function handleChange(
    field,
    value
  ) {

    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  // ==========================================================
  // VALIDATION
  // ==========================================================

  function validate() {

    if (
      !form.checklist_code?.trim()
    ) {

      alert(
        "Checklist Code is required"
      );

      return false;
    }

    if (
      !form.checklist_name?.trim()
    ) {

      alert(
        "Checklist Name is required"
      );

      return false;
    }

    if (
      !form.applicable_work_item_type
    ) {

      alert(
        "Applicable Work Item Type is required"
      );

      return false;
    }

    if (
      form.is_scoring_enabled
      &&
      (
        form.pass_percentage === ""
        ||
        form.pass_percentage === null
      )
    ) {

      alert(
        "Pass Percentage is required when scoring is enabled"
      );

      return false;
    }

    return true;
  }

  // ==========================================================
  // SAVE
  // ==========================================================

  async function handleSave() {

    if (!validate()) {
      return;
    }

    try {

      setSaving(true);

      const payload = {

        checklist_code:
          form.checklist_code,

        checklist_name:
          form.checklist_name,

        applicable_work_item_type:
          form.applicable_work_item_type,

        category:
          form.category || null,

        //department_id:
        //  form.department_id || null,

        pass_percentage:
          form.pass_percentage || null,

        is_scoring_enabled:
          form.is_scoring_enabled,

        approval_required:
          form.approval_required,

        is_global_checklist:
          form.is_global_checklist,

        description:
          form.description,

        remarks:
          form.remarks,
      };

      if (isEdit) {

        await wmsChecklistService
          .updateChecklist(
            id,
            payload
          );

      } else {

        await wmsChecklistService
          .createChecklist(
            payload
          );
      }

      navigate(
        "/wms/checklists"
      );

    } catch (err) {

      alert(
        err?.response?.data?.message
        || "Save failed"
      );

    } finally {

      setSaving(false);
    }
  }

  // ==========================================================
  // UI
  // ==========================================================

  if (loading) {

    return (
      <div className="container-fluid">
        Loading...
      </div>
    );
  }

  return (

    <div className="container-fluid">

      {/* ==================================================== */}
      {/* HEADER */}
      {/* ==================================================== */}

      <div
        className="card border-0 mb-3"
        style={{
          background:
            "linear-gradient(90deg,#0d6efd,#4f8cff)",
          color: "#fff",
        }}
      >

        <div className="card-body">

          <h3 className="mb-1">

            {isEdit
              ? "Edit Checklist"
              : "Create Checklist"}

          </h3>

          <div>

            Configure checklist
            master, sections,
            questions and scoring

          </div>

        </div>

      </div>

      {/* ==================================================== */}
      {/* DETAILS */}
      {/* ==================================================== */}

      <div className="card">

        <div className="card-header">

          <strong>
            Checklist Details
          </strong>

        </div>

        <div className="card-body">

          <div className="row">

            <div className="col-md-3 mb-3">

              <label>
                Checklist Code *
              </label>

              <input
                className="form-control"
                value={
                  form.checklist_code
                }
                onChange={(e) =>
                  handleChange(
                    "checklist_code",
                    e.target.value
                  )
                }
              />

            </div>

            <div className="col-md-5 mb-3">

              <label>
                Checklist Name *
              </label>

              <input
                className="form-control"
                value={
                  form.checklist_name
                }
                onChange={(e) =>
                  handleChange(
                    "checklist_name",
                    e.target.value
                  )
                }
              />

            </div>

            <div className="col-md-4 mb-3">

              <label>
                Applicable Work Item Type *
              </label>

              <select
                className="form-control"
                value={
                  form.applicable_work_item_type
                }
                onChange={(e) =>
                  handleChange(
                    "applicable_work_item_type",
                    e.target.value
                  )
                }
              >

                <option value="TASK">
                  Task
                </option>

                <option value="AUDIT">
                  Audit
                </option>

                <option value="OBSERVATION">
                  Observation
                </option>

                <option value="CAPA">
                  CAPA
                </option>

              </select>

            </div>

            <div className="col-md-4 mb-3">

              <label>
                Category
              </label>

              <input
                className="form-control"
                value={
                  form.category
                }
                onChange={(e) =>
                  handleChange(
                    "category",
                    e.target.value
                  )
                }
              />

            </div>

            

            <div className="col-md-4 mb-3">

              <label>
                Pass Percentage
              </label>

              <input
                type="number"
                className="form-control"
                value={
                  form.pass_percentage
                }
                onChange={(e) =>
                  handleChange(
                    "pass_percentage",
                    e.target.value
                  )
                }
              />

            </div>

            <div className="col-md-12 mb-3">

              <div className="row">

                <div className="col-md-3">

                  <div className="form-check">

                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={
                        form.is_scoring_enabled
                      }
                      onChange={(e) =>
                        handleChange(
                          "is_scoring_enabled",
                          e.target.checked
                        )
                      }
                    />

                    <label className="form-check-label">
                      Scoring Enabled
                    </label>

                  </div>

                </div>

                <div className="col-md-3">

                  <div className="form-check">

                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={
                        form.approval_required
                      }
                      onChange={(e) =>
                        handleChange(
                          "approval_required",
                          e.target.checked
                        )
                      }
                    />

                    <label className="form-check-label">
                      Approval Required
                    </label>

                  </div>

                </div>

                <div className="col-md-3">

                  <div className="form-check">

                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={
                        form.is_global_checklist
                      }
                      onChange={(e) =>
                        handleChange(
                          "is_global_checklist",
                          e.target.checked
                        )
                      }
                    />

                    <label className="form-check-label">
                      Global Checklist
                    </label>

                  </div>

                </div>

              </div>

            </div>

            <div className="col-md-12 mb-3">

              <label>
                Description
              </label>

              <textarea
                rows="3"
                className="form-control"
                value={
                  form.description
                }
                onChange={(e) =>
                  handleChange(
                    "description",
                    e.target.value
                  )
                }
              />

            </div>

            <div className="col-md-12 mb-3">

              <label>
                Remarks
              </label>

              <textarea
                rows="3"
                className="form-control"
                value={
                  form.remarks
                }
                onChange={(e) =>
                  handleChange(
                    "remarks",
                    e.target.value
                  )
                }
              />

            </div>

          </div>

        </div>

      </div>

      {/* ==================================================== */}
      {/* SECTIONS */}
      {/* ==================================================== */}

      {isEdit && (

        <ChecklistSectionsTab
          checklistId={id}
        />

      )}

      {/* ==================================================== */}
      {/* FOOTER */}
      {/* ==================================================== */}

      <div className="card mt-3">

        <div className="card-body text-end">

          <button
            className="btn btn-primary me-2"
            onClick={handleSave}
            disabled={saving}
          >

            {saving
              ? "Saving..."
              : "Save"}

          </button>

          <button
            className="btn btn-secondary"
            onClick={() =>
              navigate(
                "/wms/checklists"
              )
            }
          >
            Cancel
          </button>

        </div>

      </div>

    </div>
  );
}